<template>
  <div class="fines">
    <div class="fines__totals" v-if="totalsByCurrency.length">
      <div
        v-for="total in totalsByCurrency"
        :key="total.currency"
        class="fines__totals-item"
      >
        <span class="fines__totals-currency">{{ total.currency }}</span>
        <span class="fines__totals-amount">{{ formatCurrency(total.amount) }}</span>
      </div>
    </div>

    <Actions :items="actionsHeader" :right="true" />

    <MobileTable
      v-model:selection="store.state.fine.selectedFines"
      :label="listLabel"
      :columns="columns"
      :data="tableData"
    />
    <DesktopTable
      v-model:selection="store.state.fine.selectedFines"
      :label="listLabel"
      :columns="columns"
      :data="tableData"
    />

    <p v-if="showEmptyState" class="empty-note">No hay multas para mostrar.</p>

    <Actions :items="actionsFooter" />
  </div>
</template>

<script>
import { computed, reactive, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import store from "../../store";
import {
  mapFineRows,
  totalsByCurrency as calculateTotalsByCurrency,
} from "../../utils/accounting/fines.mjs";
import Actions from "../../components/shared/Actions.vue";
import MobileTable from "../../widgets/tables/MobileTable.vue";
import DesktopTable from "../../widgets/tables/DesktopTable.vue";
import { currency, openMode, actions } from "../../constants";

export default {
  components: {
    Actions,
    MobileTable,
    DesktopTable,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();

    const selectedBuilding = computed(() => store.state.general.selectedBuilding);
    const buildingId = computed(() => {
      const value = selectedBuilding.value;
      if (!value) return null;
      return typeof value === "object" ? value.id : value;
    });

    const fines = computed(() => store.state.fine.fines || []);
    const tableData = computed(() => mapFineRows(fines.value));
    const listLabel = computed(() => `${route.name || "Multas"} (${tableData.value.length})`);
    const totalsByCurrency = computed(() => calculateTotalsByCurrency(fines.value));

    const columns = reactive([
      { field: "apartment", header: "Apartamento", sort: true },
      { field: "concept", header: "Concepto", sort: true },
      { field: "amount", header: "Monto", currency: true, sort: true },
      { field: "currency", header: "Moneda", sort: true },
      { field: "date", header: "Fecha", date: true, sort: true },
      { field: "account", header: "Cuenta contable", sort: true },
      { field: "attachment", header: "Adjunto", sort: true },
      { field: "notifyResident", header: "Notificar", sort: true },
      { field: "detail", header: "Detalle" },
    ]);

    const showEmptyState = computed(
      () => !store.state.loading && Boolean(buildingId.value) && !tableData.value.length
    );

    const notify = (type, message) => {
      if (!message) return;
      store.commit("setToast", { type, message });
    };

    const formatCurrency = (amount) => currency.convertNumberToCurrency(amount);

    const ensureBreadcrumb = () => {
      const breadcrumb = store.state.general.breadcrumbs.find((item) => item.to === route.path);
      if (!breadcrumb) {
        store.commit("addBreadcrumb", { label: route.name, to: route.path });
      }
    };

    const requireSelection = (single = false) => {
      const selection = store.state.fine.selectedFines;
      if (!selection.length) {
        notify("warn", "Seleccione al menos una multa");
        return false;
      }
      if (single && selection.length !== 1) {
        notify("warn", "Seleccione una unica multa");
        return false;
      }
      return true;
    };

    const loadFines = async () => {
      if (!buildingId.value) return;
      const response = await store.dispatch(actions.fineActions.findAll, {
        buildingId: buildingId.value,
      });
      if (!response?.ok && response?.message) {
        notify("error", response.message);
      }
    };

    const onCreate = () => {
      store.commit("setOpenMode", openMode.CREATE);
      router.push("/contabilidad/multa/nuevo");
    };

    const onModify = () => {
      if (!requireSelection(true)) return;
      const selected = store.state.fine.selectedFines[0] || null;
      if (selected) {
        store.commit("setCurrentFine", selected);
      }
      store.commit("setOpenMode", openMode.MODIFY);
      router.push("/contabilidad/multa/modificar");
    };

    const onDelete = async () => {
      if (!requireSelection()) return;
      if (!buildingId.value) {
        notify("error", "Seleccione un edificio para continuar");
        return;
      }
      const confirmed = window.confirm("Eliminar las multas seleccionadas?");
      if (!confirmed) return;

      store.commit("setLoading", true);
      try {
        const responses = await Promise.all(
          store.state.fine.selectedFines.map((fine) =>
            store.dispatch(actions.fineActions.delete, {
              id: fine.id,
              buildingId: buildingId.value,
            })
          )
        );
        const hasError = responses.some((response) => !response?.ok);
        if (hasError) {
          notify("error", "No fue posible eliminar algunas multas.");
        } else {
          notify("success", "Multas eliminadas correctamente.");
          await loadFines();
        }
      } finally {
        store.commit("setLoading", false);
        store.commit("setSelectedFines", []);
      }
    };

    const onDownloadExcel = async () => {
      if (!tableData.value.length) {
        notify("warn", "No hay datos para exportar");
        return;
      }
      store.commit("setLoading", true);
      try {
        const request = {
          fileName: "multas",
          headers: columns.map((column) => column.header),
          data: tableData.value.map((row) => [
            row.apartment,
            row.concept,
            row.amount,
            row.currency,
            row.date,
            row.account,
            row.attachment,
            row.notifyResident,
            row.detail,
          ]),
        };
        await store.dispatch(actions.fineActions.downloadExcel, request);
      } finally {
        store.commit("setLoading", false);
      }
    };

    const goToConcept = () => {
      router.push("/contabilidad/concepto/nuevo");
    };

    const actionsHeader = reactive([
      {
        name: "Descargar Excel",
        action: onDownloadExcel,
        hidden: true,
      },
    ]);

    const actionsFooter = reactive([
      {
        name: "Nueva",
        action: onCreate,
        hidden: router.getRoutes().find((item) => item.path === "/contabilidad/multa/nuevo"),
      },
      {
        name: "Modificar",
        action: onModify,
        hidden: router.getRoutes().find((item) => item.path === "/contabilidad/multa/modificar"),
      },
      {
        name: "Eliminar",
        action: onDelete,
        hidden: true,
      },
      {
        name: "Nuevo concepto",
        action: goToConcept,
        hidden: router.getRoutes().find((item) => item.path === "/contabilidad/concepto/nuevo"),
      },
    ]);

    onMounted(async () => {
      store.commit("setLoading", true);
      store.commit("setSelectedFines", []);
      ensureBreadcrumb();
      try {
        await loadFines();
      } finally {
        store.commit("setLoading", false);
      }
    });

    watch(buildingId, async (value, oldValue) => {
      if (value && value !== oldValue) {
        store.commit("setLoading", true);
        store.commit("setSelectedFines", []);
        try {
          await loadFines();
        } finally {
          store.commit("setLoading", false);
        }
      }
    });

    return {
      store,
      columns,
      tableData,
      listLabel,
      totalsByCurrency,
      formatCurrency,
      actionsHeader,
      actionsFooter,
      showEmptyState,
    };
  },
};
</script>

<style scoped>
.fines {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-bottom: 2rem;
}

.fines__totals {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin: 0 2rem;
  background-color: #f7f9fb;
  padding: 1rem;
  border-radius: 8px;
}

.fines__totals-item {
  display: flex;
  flex-direction: column;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.fines__totals-currency {
  font-size: 0.8rem;
  text-transform: uppercase;
  color: var(--primary-dark);
}

.fines__totals-amount {
  font-size: 1.1rem;
  font-weight: 600;
}

.empty-note {
  margin: 1rem 0;
  text-align: center;
  color: #64748b;
}

@media screen and (min-width: 1440px) {
  .fines__totals {
    margin: 0 4rem;
  }
}
</style>







